import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import {
  fetchUserCoursesSafe,
  fetchUserInfoById,
  fetchUserInfoSafe,
  fetchUserProductsSafe,
  UserCourse,
  UserInfo,
  UserProduct,
} from '../services/userManager';

// Types
export interface User {
  id: number;
  email: string;
  name?: string;
  permissions?: string[];
  roles?: string[];
  [key: string]: any;
}

export interface AuthState {
  // Core state
  user: User | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Request management
  isRequestInProgress: boolean;
  lastFetchTime: number | null;

  // User data state
  userProducts: UserProduct[];
  userCourses: UserCourse[];
  userInfo: UserInfo | null;
  isLoadingProducts: boolean;
  isLoadingCourses: boolean;
  isLoadingUserInfo: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setRequestInProgress: (inProgress: boolean) => void;

  // Auth operations
  login: (user: User) => void;
  updateUser: (user: Partial<User>) => void;
  logout: () => void;
  clearError: () => void;

  // API operations
  fetchUser: () => Promise<void>;
  initialize: () => Promise<void>;

  // User data operations
  fetchUserProducts: (userId: string | number) => Promise<void>;
  fetchUserCourses: (userId: string | number) => Promise<void>;
  fetchUserInfo: (userId: string | number) => Promise<void>;
  clearUserData: () => void;
}

// Initial state
const initialState = {
  user: null,
  isLoggedIn: false,
  isLoading: false,
  isInitialized: false,
  error: null,
  isRequestInProgress: false,
  lastFetchTime: null,
  userProducts: [],
  userCourses: [],
  userInfo: null,
  isLoadingProducts: false,
  isLoadingCourses: false,
  isLoadingUserInfo: false,
};

// Create the store with all optimizations
export const useAuthStore = create<AuthState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      ...initialState,

      // Basic setters
      setUser: user =>
        set(state => {
          state.user = user;
          state.isLoggedIn = !!user;
          state.error = null;
        }),

      setLoading: loading =>
        set(state => {
          state.isLoading = loading;
        }),

      setError: error =>
        set(state => {
          state.error = error;
        }),

      setRequestInProgress: inProgress =>
        set(state => {
          state.isRequestInProgress = inProgress;
        }),

      // Auth operations
      login: user =>
        set(state => {
          state.user = user;
          state.isLoggedIn = true;
          state.error = null;
          state.isLoading = false;
        }),

      updateUser: user =>
        set(state => {
          if (state.user) {
            state.user = { ...state.user, ...user };
          }
        }),

      logout: () => {
        set(state => {
          state.user = null;
          state.isLoggedIn = false;
          state.error = null;
          state.isLoading = false;
          state.isInitialized = true;
          state.lastFetchTime = null;
          state.isRequestInProgress = false;
          // Clear user data as well
          state.userProducts = [];
          state.userCourses = [];
          state.userInfo = null;
          state.isLoadingProducts = false;
          state.isLoadingCourses = false;
          state.isLoadingUserInfo = false;
        });
      },

      clearError: () =>
        set(state => {
          state.error = null;
        }),

      // API operations
      fetchUser: async () => {
        const state = get();

        // Prevent multiple concurrent requests
        if (state.isRequestInProgress) {
          return;
        }

        // Throttle requests (max 1 per 5 seconds)
        const now = Date.now();
        if (state.lastFetchTime && now - state.lastFetchTime < 5000) {
          return;
        }

        set(draft => {
          draft.isRequestInProgress = true;
          draft.isLoading = true;
          draft.error = null;
          draft.lastFetchTime = now;
        });

        try {
          const userData = await fetchUserInfoSafe();

          if (userData) {
            set(draft => {
              draft.user = userData;
              draft.isLoggedIn = true;
              draft.isLoading = false;
              draft.isInitialized = true;
              draft.error = null;
            });
          } else {
            set(draft => {
              draft.user = null;
              draft.isLoggedIn = false;
              draft.isLoading = false;
              draft.isInitialized = true;
              draft.error = null;
            });
          }
        } catch (error) {
          console.error('❌ Error fetching user:', error);
          set(draft => {
            draft.user = null;
            draft.isLoggedIn = false;
            draft.isLoading = false;
            draft.isInitialized = true;
            draft.error =
              error instanceof Error ? error.message : 'Failed to fetch user';
          });
        } finally {
          set(draft => {
            draft.isRequestInProgress = false;
          });
        }
      },

      initialize: async () => {
        const state = get();

        if (state.isInitialized) {
          return;
        }

        // Set initialized to true immediately to prevent multiple initializations
        set(draft => {
          draft.isInitialized = true;
        });

        await get().fetchUser();
      },

      // User data operations
      fetchUserProducts: async userId => {
        // Check if already loading for this user to avoid duplicate requests
        const currentState = get();
        if (currentState.isLoadingProducts) {
          return;
        }

        set(draft => {
          draft.isLoadingProducts = true;
          draft.error = null;
        });

        try {
          const products = await fetchUserProductsSafe(userId);
          set(draft => {
            draft.userProducts = products;
            draft.isLoadingProducts = false;
          });
        } catch (error) {
          console.error('❌ Error fetching user products:', error);
          set(draft => {
            draft.isLoadingProducts = false;
            draft.error =
              error instanceof Error
                ? error.message
                : 'Failed to fetch user products';
          });
        }
      },

      fetchUserCourses: async userId => {
        // Check if already loading for this user to avoid duplicate requests
        const currentState = get();
        if (currentState.isLoadingCourses) {
          return;
        }

        set(draft => {
          draft.isLoadingCourses = true;
          draft.error = null;
        });

        try {
          const courses = await fetchUserCoursesSafe(userId);
          set(draft => {
            draft.userCourses = courses;
            draft.isLoadingCourses = false;
          });
        } catch (error) {
          console.error('❌ Error fetching user courses:', error);
          set(draft => {
            draft.isLoadingCourses = false;
            draft.error =
              error instanceof Error
                ? error.message
                : 'Failed to fetch user courses';
          });
        }
      },

      fetchUserInfo: async userId => {
        // Check if already loading for this user to avoid duplicate requests
        const currentState = get();
        if (currentState.isLoadingUserInfo) {
          return;
        }

        set(draft => {
          draft.isLoadingUserInfo = true;
          draft.error = null;
        });

        try {
          const userInfo = await fetchUserInfoById(userId);
          set(draft => {
            draft.userInfo = userInfo;
            draft.isLoadingUserInfo = false;
          });
        } catch (error) {
          console.error('❌ Error fetching user info:', error);
          set(draft => {
            draft.isLoadingUserInfo = false;
            draft.error =
              error instanceof Error
                ? error.message
                : 'Failed to fetch user info';
          });
        }
      },

      clearUserData: () => {
        set(draft => {
          draft.userProducts = [];
          draft.userCourses = [];
          draft.userInfo = null;
          draft.isLoadingProducts = false;
          draft.isLoadingCourses = false;
          draft.isLoadingUserInfo = false;
        });
      },
    }))
  )
);

// Selectors for performance optimization
export const useAuthUser = () => useAuthStore(state => state.user);
export const useAuthLoading = () => useAuthStore(state => state.isLoading);
export const useAuthError = () => useAuthStore(state => state.error);
export const useAuthLoggedIn = () => useAuthStore(state => state.isLoggedIn);
export const useAuthInitialized = () =>
  useAuthStore(state => state.isInitialized);

// User data selectors
export const useUserProducts = () => useAuthStore(state => state.userProducts);
export const useUserCourses = () => useAuthStore(state => state.userCourses);
export const useUserInfo = () => useAuthStore(state => state.userInfo);
export const useUserProductsLoading = () =>
  useAuthStore(state => state.isLoadingProducts);
export const useUserCoursesLoading = () =>
  useAuthStore(state => state.isLoadingCourses);
export const useUserInfoLoading = () =>
  useAuthStore(state => state.isLoadingUserInfo);

// Permission selectors - computed as separate hooks to avoid infinite loops
export const useHasPermission = (permission: string) => {
  const user = useAuthStore(state => state.user);
  return user?.permissions?.includes(permission) ?? false;
};

export const useHasRole = (role: string) => {
  const user = useAuthStore(state => state.user);
  return user?.roles?.includes(role) ?? false;
};

export const useIsAuthenticated = () => {
  const user = useAuthStore(state => state.user);
  const isLoggedIn = useAuthStore(state => state.isLoggedIn);
  return isLoggedIn && !!user;
};

// Action selectors - memoized to prevent infinite loops
export const useAuthActions = () => {
  const login = useAuthStore(state => state.login);
  const logout = useAuthStore(state => state.logout);
  const updateUser = useAuthStore(state => state.updateUser);
  const fetchUser = useAuthStore(state => state.fetchUser);
  const initialize = useAuthStore(state => state.initialize);
  const clearError = useAuthStore(state => state.clearError);
  const fetchUserProducts = useAuthStore(state => state.fetchUserProducts);
  const fetchUserCourses = useAuthStore(state => state.fetchUserCourses);
  const fetchUserInfo = useAuthStore(state => state.fetchUserInfo);
  const clearUserData = useAuthStore(state => state.clearUserData);

  return {
    login,
    logout,
    updateUser,
    fetchUser,
    initialize,
    clearError,
    fetchUserProducts,
    fetchUserCourses,
    fetchUserInfo,
    clearUserData,
  };
};

// Full state selector (use sparingly) - memoized to prevent infinite loops
export const useAuthState = () => {
  const user = useAuthStore(state => state.user);
  const isLoggedIn = useAuthStore(state => state.isLoggedIn);
  const isLoading = useAuthStore(state => state.isLoading);
  const isInitialized = useAuthStore(state => state.isInitialized);
  const error = useAuthStore(state => state.error);
  const isRequestInProgress = useAuthStore(state => state.isRequestInProgress);
  const lastFetchTime = useAuthStore(state => state.lastFetchTime);
  const userProducts = useAuthStore(state => state.userProducts);
  const userCourses = useAuthStore(state => state.userCourses);
  const userInfo = useAuthStore(state => state.userInfo);
  const isLoadingProducts = useAuthStore(state => state.isLoadingProducts);
  const isLoadingCourses = useAuthStore(state => state.isLoadingCourses);
  const isLoadingUserInfo = useAuthStore(state => state.isLoadingUserInfo);

  return {
    user,
    isLoggedIn,
    isLoading,
    isInitialized,
    error,
    isRequestInProgress,
    lastFetchTime,
    userProducts,
    userCourses,
    userInfo,
    isLoadingProducts,
    isLoadingCourses,
    isLoadingUserInfo,
  };
};
